import { OrgbrainFatal } from '@orgbrain/lib-data';
import { hasPermission, PermissionEnum, RoleMapType, sortByRoleHelper } from '@orgbrain/roles';
import { CurrentUser } from './lib-admin';

export const getOrgFromCurrentUser = function ({
  currentUser,
  organizationId,
  orgId,
}: {
  currentUser: CurrentUser;
  organizationId?: string;
  orgId?: string;
}) {
  const userOrgs = currentUser.organizations || [];
  const org = userOrgs.find((org) => {
    if (organizationId) {
      return org._id.toString() === organizationId.toString();
    } else if (orgId) {
      return org.orgId === orgId;
    }
    return null;
  });

  return org;
};

export const getBestOrgFromCurrentUser = function ({
  currentUser,
  allowedOrgIds,
}: {
  currentUser: CurrentUser;
  allowedOrgIds: string[];
}) {
  let selectedOrg = null;
  let selectedBestStat = -1;
  for (const orgMongoId of allowedOrgIds) {
    const myOrg = getOrgFromCurrentUser({ currentUser, organizationId: orgMongoId });

    const currentStat =
      Object.keys(myOrg?.roleMap || {}).length && sortByRoleHelper({ roles: myOrg && myOrg.roles });

    if (currentStat > selectedBestStat) {
      selectedBestStat = currentStat;
      selectedOrg = myOrg;
    }
  }

  return selectedOrg;
};

export function getOrganizationRoleMapFromUserAndOrgId(
  orgId: string,
  user: CurrentUser
): RoleMapType {
  const organizationId = user.orgIdToOrganizationIdMap[orgId];
  if (!organizationId) {
    throw new OrgbrainFatal(
      ` getOrganizationRoleMapFromUserAndOrgId    no organization for ${orgId} `
    );
  }
  return getOrganizationRoleMapFromUser(organizationId, user);
}

export function getOrganizationRoleMapFromUser(
  organizationId: string,
  user: CurrentUser
): RoleMapType {
  const org = getOrgFromCurrentUser({ currentUser: user, organizationId });
  return org?.roleMap || ({} as RoleMapType);
}

// BY default throw error if not authorized.
// DEPRECATED please use PermissionService
export const hasCurrentUserPermissions = function ({
  permissions,
  currentUser,
  organizationId, // PLease use this.
  orgId, // For legacy usage we can also use this
  throwError,
}: {
  permissions: PermissionEnum[] | 'ANYROLE';
  currentUser: CurrentUser;
  organizationId?: string;
  orgId?: string;
  throwError?: boolean;
}) {
  if (!currentUser || !currentUser.organizations) {
    return false;
  }

  const thisOrg = getOrgFromCurrentUser({ currentUser, orgId, organizationId });
  const rolesInOrg = (thisOrg && thisOrg.roles) || [];

  if (permissions === 'ANYROLE') {
    const hasRole = rolesInOrg.length > 0;

    if (!hasRole && throwError) {
      throw Error(' Authorization not granted (no role)');
    }
    return hasRole;
  }

  const roleMap: RoleMapType = (thisOrg && thisOrg.roleMap) || ({} as RoleMapType);
  for (const perm of permissions as PermissionEnum[]) {
    if (hasPermission(perm, rolesInOrg, roleMap)) {
      return true;
    }
  }
  if (throwError) {
    throw Error(' Authorization not granted (no permission)');
  }

  return false;
};

export const isMeetingAdminInOrg = function ({
  currentUser,
  orgId,
  organizationId,
}: {
  currentUser: CurrentUser;
  organizationId?: string;
  orgId?: string;
}) {
  return hasCurrentUserPermissions({
    permissions: [PermissionEnum.MEETING_ADMIN],
    currentUser,
    orgId,
    organizationId,
  });
};

export type CurrentUserPermissionProps = {
  permissions: PermissionEnum[] | 'ANYROLE' | 'NONE';
  currentUser: CurrentUser;
  organizationId?: string;
  orgId?: string;
  throwError?: boolean;
};

export const getCurrentUserPermissions = function ({
  permissions,
  currentUser,
  organizationId,
  throwError,
}: CurrentUserPermissionProps) {
  const result: { any: boolean; [key: string]: boolean } = { any: false };

  if (!currentUser) throw Error('Authorization not granted (no user)');
  if (permissions === 'NONE') return { any: true };

  if (!currentUser.organizations) throw Error('User has no organizations (no orgs)');

  const thisOrg = getOrgFromCurrentUser({ currentUser, organizationId });
  const rolesInOrg = (thisOrg && thisOrg.roles) || [];

  if (permissions === 'ANYROLE') {
    const hasRole = rolesInOrg.length > 0;
    result.any = hasRole;

    if (!hasRole && throwError) throw Error('Authorization not granted (no role 2)');
    return result;
  }

  const roleMap: RoleMapType = (thisOrg && thisOrg.roleMap) || ({} as RoleMapType);
  let anyPermissionGranted = false;

  for (const perm of permissions as PermissionEnum[]) {
    const hasPerm = hasPermission(perm, rolesInOrg, roleMap);
    result[perm] = hasPerm;
    if (hasPerm) anyPermissionGranted = true;
  }

  result.any = anyPermissionGranted;

  if (!anyPermissionGranted && throwError) {
    throw Error('Authorization not granted (no permission 2)');
  }

  return result;
};
